import { List, ShowButton, useListContext } from "react-admin";
import { MenuItem } from "@mui/material";
import { useEffect, useState } from "react";
import CityResponse from "../../interfaces/City/CityResponse";
import CityService from "../../services/CityService";
import { UserType } from "../../enums/common";
import { PaymentStatusEnum } from "../../enums/PaymentStatusEnum";
import { MultiDayChefitStatus } from "../../enums/MultiDayChefitStatus";
import {
  FilterContainer,
  FilterInput,
  FilterSection,
  FilterTextField,
  FilterWrapper,
  FIlterHeader,
  SectionHeader,
  SectionTitle,
  WorkSpaceTitleRow,
} from "../../styles/commonStyles";
import { FilterIcon } from "../Svg";
import TransactionButton from "../Common/TransactionButton";
import CallButton from "../Common/CallButton/CallButton";
import CommonListTable, { CommonListColumn } from "../Common/CommonListTable";

const MultiDayChefitFilters = ({ cities }: { cities: CityResponse[] }) => {
  const { filterValues, setFilters, isLoading } = useListContext();

  const [packIdInput, setPackIdInput] = useState<string>(
    filterValues.comboOrderId ?? ""
  );
  const [customerNameInput, setCustomerNameInput] = useState<string>(
    filterValues.customerName ?? ""
  );
  const [customerPhoneInput, setCustomerPhoneInput] = useState<string>(
    filterValues.customerPhoneNumber ?? ""
  );
  const [cookNameInput, setCookNameInput] = useState<string>(
    filterValues.cookName ?? ""
  );
  const [cookPhoneInput, setCookPhoneInput] = useState<string>(
    filterValues.cookPhoneNumber ?? ""
  );
  const [sectorInput, setSectorInput] = useState<string>(
    filterValues.sector ?? ""
  );
  const [cancelledByInput, setCancelledByInput] = useState<string>(
    filterValues.cancelledBy ?? ""
  );

  // Sync all text inputs when filters are cleared externally
  useEffect(() => {
    setPackIdInput(filterValues.comboOrderId ?? "");
    setCustomerNameInput(filterValues.customerName ?? "");
    setCustomerPhoneInput(filterValues.customerPhoneNumber ?? "");
    setCookNameInput(filterValues.cookName ?? "");
    setCookPhoneInput(filterValues.cookPhoneNumber ?? "");
    setSectorInput(filterValues.sector ?? "");
    setCancelledByInput(filterValues.cancelledBy ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterValues.comboOrderId,
    filterValues.customerName,
    filterValues.customerPhoneNumber,
    filterValues.cookName,
    filterValues.cookPhoneNumber,
    filterValues.sector,
    filterValues.cancelledBy,
  ]);

  // Single debounce for all text inputs
  useEffect(() => {
    const timer = setTimeout(() => {
      const next: any = { ...filterValues };
      const pairs: Array<[string, string]> = [
        ["comboOrderId", packIdInput],
        ["customerName", customerNameInput],
        ["customerPhoneNumber", customerPhoneInput],
        ["cookName", cookNameInput],
        ["cookPhoneNumber", cookPhoneInput],
        ["sector", sectorInput],
        ["cancelledBy", cancelledByInput],
      ];
      let changed = false;
      for (const [key, value] of pairs) {
        if ((filterValues[key] ?? "") !== value) {
          changed = true;
          if (value) next[key] = value;
          else delete next[key];
        }
      }
      if (changed) setFilters(next, {}, false);
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    packIdInput,
    customerNameInput,
    customerPhoneInput,
    cookNameInput,
    cookPhoneInput,
    sectorInput,
    cancelledByInput,
  ]);

  const applyDropdown = (key: string, value: string | number | "") => {
    const next: any = { ...filterValues };
    if (value === "" || value === null || value === undefined) {
      delete next[key];
    } else {
      next[key] = value;
    }
    setFilters(next, {}, false);
  };

  return (
    <FilterWrapper>
      <FIlterHeader>
        <FilterSection style={{ marginBottom: "0" }}>
          <SectionHeader>
            <WorkSpaceTitleRow>
              <FilterIcon color="#1976d2" height={20} width={20} />
              <SectionTitle>Filters</SectionTitle>
            </WorkSpaceTitleRow>
          </SectionHeader>
          <TransactionButton
            name="Clear Filter"
            onClickHandler={() => setFilters({}, {}, false)}
          />
        </FilterSection>
      </FIlterHeader>

      <FilterContainer style={{ flexWrap: "wrap", rowGap: "12px" }}>
        <FilterTextField
          select
          label="Status"
          size="small"
          variant="outlined"
          value={filterValues.status ?? ""}
          disabled={isLoading}
          onChange={(e) => applyDropdown("status", e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          {Object.values(MultiDayChefitStatus).map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </FilterTextField>

        <FilterTextField
          select
          label="Payment Status"
          size="small"
          variant="outlined"
          value={filterValues.paymentStatus ?? ""}
          disabled={isLoading}
          onChange={(e) => applyDropdown("paymentStatus", e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All</MenuItem>
          {Object.values(PaymentStatusEnum).map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </FilterTextField>

        <FilterTextField
          select
          label="City"
          size="small"
          variant="outlined"
          value={filterValues.cityId ?? ""}
          disabled={isLoading}
          onChange={(e) =>
            applyDropdown(
              "cityId",
              e.target.value === "" ? "" : Number(e.target.value)
            )
          }
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          {cities.map((city: any) => (
            <MenuItem key={city?.id} value={city?.id}>
              {city?.name}
            </MenuItem>
          ))}
        </FilterTextField>

        <FilterInput
          value={packIdInput}
          type="number"
          size="small"
          variant="outlined"
          onChange={(e) => {
            const val = e.target.value;
            if (val === "" || (/^\d+$/.test(val) && Number(val) >= 0)) {
              setPackIdInput(val);
            }
          }}
          label="Pack Id"
          InputProps={{ inputProps: { min: 0 } }}
        />

        <FilterInput
          value={customerNameInput}
          size="small"
          variant="outlined"
          onChange={(e) => setCustomerNameInput(e.target.value)}
          label="Customer Name"
        />

        <FilterInput
          value={customerPhoneInput}
          size="small"
          variant="outlined"
          onChange={(e) => setCustomerPhoneInput(e.target.value)}
          label="Customer Phone"
        />

        <FilterInput
          value={cookNameInput}
          size="small"
          variant="outlined"
          onChange={(e) => setCookNameInput(e.target.value)}
          label="Cook Name"
        />

        <FilterInput
          value={cookPhoneInput}
          size="small"
          variant="outlined"
          onChange={(e) => setCookPhoneInput(e.target.value)}
          label="Cook Phone"
        />

        <FilterInput
          value={sectorInput}
          size="small"
          variant="outlined"
          onChange={(e) => setSectorInput(e.target.value)}
          label="Sector"
        />

        <FilterInput
          value={cancelledByInput}
          size="small"
          variant="outlined"
          onChange={(e) => setCancelledByInput(e.target.value)}
          label="Cancelled By"
        />
      </FilterContainer>
    </FilterWrapper>
  );
};

const columns: CommonListColumn[] = [
  {
    label: "Actions",
    stopRowClick: true,
    render: (row: any) => <ShowButton record={row} />,
  },
  { label: "Id", field: "id" },
  { label: "Customer Name", field: "customerName" },
  {
    label: "Customer Phone Number",
    stopRowClick: true,
    render: (row: any) => (
      <CallButton
        toPhoneNumber={row?.customerPhoneNumber}
        userType={UserType.CUSTOMER}
        referenceId={row?.id}
        referenceModelName={"COUPON_ORDER"}
      />
    ),
  },
  { label: "Sector", field: "areaName" },
  { label: "Locality", field: "localityName" },
  {
    label: "Cook Name",
    render: (row: any) => {
      if (!row?.cook?.cookFullProfile?.firstName) return "Not Available";
      return `${row?.cook?.cookFullProfile?.firstName} ${row?.cook?.cookFullProfile?.lastName}`;
    },
  },
  {
    label: "Cook Phone Number",
    stopRowClick: true,
    render: (row: any) => (
      <CallButton
        toPhoneNumber={row?.cook?.mobile}
        userType={UserType.COOK}
        referenceId={row?.id}
        referenceModelName={"COUPON_ORDER"}
      />
    ),
  },
  { label: "Status", field: "status", type: "status" },
  { label: "Cancellation Reason", field: "cancellationReason" },
  { label: "Payment Status", field: "paymentStatus", type: "status" },
  { label: "Created At", field: "createdAt", type: "datetime", minWidth: 175 },
];

const MultiDayChefitTable = () => {
  const { data, isLoading, page, perPage, total, setPage, setPerPage } =
    useListContext();
  return (
    <CommonListTable
      title="Multi Day Chefit"
      columns={columns}
      data={data ?? []}
      loading={isLoading}
      page={page - 1}
      rowsPerPage={perPage}
      total={total ?? 0}
      onPageChange={(newPage) => setPage(newPage + 1)}
      onRowsPerPageChange={(rows) => {
        setPerPage(rows);
        setPage(1);
      }}
      maxTableHeight="calc(100vh - 320px)"
    />
  );
};

const cityService = new CityService();

const MultiDayChefitList = (props: any) => {
  const [cities, setCities] = useState<CityResponse[]>([]);
  useEffect(() => {
    cityService
      .fetchAllCities(100, 0, { isCurrentlyServing: "YES" }, {})
      .then((res) => {
        setCities(res?.data);
      });
  }, []);

  return (
    <List
      {...props}
      sort={{ field: "id", order: "DESC" }}
      pagination={false}
      sx={{
        "& .RaList-content": {
          boxShadow: "none",
          backgroundColor: "transparent",
        },
      }}
    >
      <MultiDayChefitFilters cities={cities} />

      <br />

      <MultiDayChefitTable />
    </List>
  );
};

export default MultiDayChefitList;
